// The reading, in a sentence a person can read.
//
// The reading loop turns a trace into typed, quote-anchored operations. That is
// a ledger, not an account: it says a hundred true things and never says what
// the session was FOR. This writes the account -- one line of outcome and a
// handful of steps -- for someone who was not watching.
//
// Two rules give it whatever trust it has.
//
// It reads the CORRECTED reading, never the raw trace. The operations it is
// given are the drafters' claims the judge kept plus the additions the judge
// found and the quote gate anchored; every one of them has already been checked
// against the session's own bytes. Summarising raw turns instead would throw
// that away and put a narrator back in front of unverified text, which is the
// thing the whole loop exists to avoid.
//
// And every literal it names is CHECKED. Narrative is exactly where a model
// invents a plausible path or endpoint, so a line naming a file, command, URL
// or query that is not in the operations list is dropped and counted -- the
// same mechanical discipline as the verbatim-quote gate on additions, applied
// to prose. What survives names only things that happened.
//
// This is the dossier's fourth epistemic tier ("modeled"). Like the third it is
// for display and triage only: it must never feed provenance, joins, or another
// signal's input.

import { runSubAgent } from "./subAgent.js";
import { normalizeForMatch, windowBudgetChars } from "./traceEvaluation.js";
import { lookupCatalogModel } from "./modelCatalog.js";

// Operations handed to the summariser. Past this the account stops being an
// account -- and the cap is stated on the surface rather than silently applied,
// because a summary of half a session that does not say so is a false one.
const OPS_CAP = 400;
const PROMPT_CAP = 600;
const PROMPTS_MAX = 12;

const clip = (s, n) => {
  const t = String(s ?? "");
  return t.length > n ? `${t.slice(0, n)}...` : t;
};

/**
 * The corrected reading as a flat operation list, in the order it happened.
 *
 * Accepted claims and verified additions only. A DISPUTED claim is not
 * something the agent did -- it is something the drafters got wrong -- and an
 * unruled one was never answered, so neither may reach a surface that says
 * "here is what happened".
 */
export function summaryOps(report) {
  const out = [];
  for (const call of report?.calls || []) {
    if (!call.judged) continue;
    const verdicts = call.verdicts || [];
    (call.claims || []).forEach((c, i) => {
      const v = verdicts.find((x) => x.claim === i);
      if (v?.verdict !== "accept") return;
      out.push({ turn: call.turnIndex, kind: c.kind, subject: c.subject, via: call.name });
    });
    for (const m of call.missing || []) {
      out.push({ turn: call.turnIndex, kind: m.kind, subject: m.subject, via: call.name });
    }
  }
  return out;
}

/** The session's own asks, which are what the operations were in service of. */
export function summaryPrompts(turns) {
  const out = [];
  for (const turn of turns || []) {
    if (turn?.role !== "user") continue;
    const text = (turn.blocks || [])
      .filter((b) => b?.type === "text" && b.text)
      .map((b) => b.text)
      .join("\n")
      .trim();
    if (text) out.push(clip(text, PROMPT_CAP));
  }
  return out.slice(0, PROMPTS_MAX);
}

// A token worth checking: something that looks like a machine target rather
// than a word. Paths, endpoints, URLs, dotted filenames, and anything the model
// put in backticks — the shapes a summary invents when it is guessing.
// The bare-filename branch takes every dotted segment, not just the last one:
// `[\w-]+` cannot cross a dot, so `auth.test.js` was captured as `test.js` --
// a suffix half a repository shares, checked in place of the actual name.
const LITERAL = /`([^`]+)`|((?:[\w@~.-]*\/)+[\w@.:#-]+)|(\b(?:[\w-]+\.)+(?:js|jsx|ts|tsx|py|rs|go|md|json|yaml|yml|toml|tf|sql|sh|conf|txt|html|css)\b)/gi;

/** The literals a line names, as the gate sees them. */
export function namedLiterals(line) {
  const out = [];
  for (const m of String(line || "").matchAll(LITERAL)) {
    // Sentence punctuation is not part of the name. "variables.tf." must be
    // checked as "variables.tf", or every line that ends on a filename fails a
    // gate it should pass -- the gate is against invention, not against prose.
    const raw = (m[1] || m[2] || m[3] || "").trim().replace(/[.,;:!?)\]}'"]+$/, "");
    // A bare "and/or" style slash ("read/write") is prose, not a target.
    if (!raw || raw.length < 3) continue;
    out.push(raw);
  }
  return out;
}

/**
 * Whether `needle` appears in `haystack` as a NAME rather than as any old run
 * of characters.
 *
 * A plain substring test is what makes a suffix dangerous: "auth.test.js"
 * inside "foo.test.js", or "config.js" inside "appconfig.js", is a different
 * file wearing a shared tail. An occurrence counts only when nothing on either
 * side could continue the name -- so a shorthand that starts a real segment
 * ("variables.tf" of ".../msk/variables.tf") still passes, because the gate is
 * against invention, not brevity.
 */
function appearsAsName(haystack, needle) {
  if (!needle) return false;
  const cont = /[\w.\-]/;
  for (let i = haystack.indexOf(needle); i !== -1; i = haystack.indexOf(needle, i + 1)) {
    const before = i === 0 ? "" : haystack[i - 1];
    const after = haystack[i + needle.length] || "";
    if (!cont.test(before) && !cont.test(after)) return true;
  }
  return false;
}

/** Everything a summary is allowed to name, from the operations themselves. */
export function allowedText(ops) {
  return normalizeForMatch((ops || []).map((o) => `${o.subject} ${o.via || ""}`).join("\n")).toLowerCase();
}

/**
 * The gate. A line survives only when every literal it names appears in the
 * operations; anything else is a name the summariser supplied itself.
 */
export function gateSummary(lines, ops) {
  const allowed = allowedText(ops);
  const kept = [];
  const dropped = [];
  for (const line of lines || []) {
    const text = String(line || "").trim();
    if (!text) continue;
    const bad = namedLiterals(text).filter(
      (lit) => !appearsAsName(allowed, normalizeForMatch(lit).toLowerCase()),
    );
    if (bad.length) dropped.push({ line: text, unknown: bad });
    else kept.push(text);
  }
  return { kept, dropped };
}

const SUMMARY_SCHEMA = {
  type: "object",
  properties: {
    outcome: { type: "string", description: "One plain sentence: what this session came to." },
    steps: {
      type: "array",
      description: "3-8 lines, in order, each one thing the agent did.",
      items: { type: "string" },
    },
  },
  required: ["outcome", "steps"],
};

export function summarySystemPrompt() {
  return [
    "You describe what an AI agent did in one working session, for a colleague who was not watching and has no time to read the transcript.",
    "",
    "You get the session's own prompts, and a list of the operations the agent performed — files read and written, commands run, APIs called, queries issued. That list has already been checked against the session's raw bytes: it is what happened, and it is all you know.",
    "",
    "Write an OUTCOME — one plain sentence naming what the session came to — and 3 to 8 STEPS, in order, each naming one thing the agent did.",
    "",
    "Name only what the operations list contains. Every file, endpoint, command or query you mention must appear there; a name that is not in the list will be discarded along with the line it is on.",
    "",
    "Say what was done, never whether it was done well, and never what the agent was thinking or intending. If the operations do not support a claim, leave it out.",
    "",
    "Plain sentences a person would say out loud. No headings, no markdown, no bullet characters — the surface adds those.",
  ].join("\n");
}

/**
 * Write the account. Returns `{status, summary}` where summary carries the
 * gated lines and what the gate discarded, so the surface can report its own
 * fallibility instead of presenting a filtered narrative as a whole one.
 */
export async function runReadingSummary({ report, turns, creds = null, model = null, signal }) {
  const ops = summaryOps(report);
  if (!ops.length) return { status: "error", error: new Error("the reading found no confirmed work to summarise") };

  const capped = ops.slice(0, OPS_CAP);
  const prompts = summaryPrompts(turns);
  const contextWindow = lookupCatalogModel(creds?.provider, model)?.contextWindow || null;
  const budget = windowBudgetChars({ contextWindow, prefixChars: 2000 });

  const task = clip([
    prompts.length ? "WHAT THE SESSION WAS ASKED:" : "",
    ...prompts.map((p, i) => `${i + 1}. ${p}`),
    "",
    `OPERATIONS (${capped.length}${ops.length > capped.length ? ` of ${ops.length}` : ""}, in order):`,
    ...capped.map((o) => `turn ${o.turn + 1} · ${o.kind} · ${o.subject}`),
  ].filter(Boolean).join("\n"), budget);

  let r;
  try {
    r = await runSubAgent({
      role: "primary",
      model: model || null,
      signal,
      tools: [],
      outputSchema: SUMMARY_SCHEMA,
      systemPrompt: summarySystemPrompt(),
      task,
    });
  } catch (e) {
    r = { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
  }
  if (r.status !== "done" || !r.result) {
    return { status: "error", error: r.error || new Error("the summary returned nothing") };
  }

  const raw = r.result;
  const gatedSteps = gateSummary(Array.isArray(raw.steps) ? raw.steps : [], capped);
  const gatedOutcome = gateSummary([raw.outcome], capped);
  return {
    status: "done",
    summary: {
      outcome: gatedOutcome.kept[0] || null,
      steps: gatedSteps.kept,
      // What the gate refused, kept as a count rather than hidden: a summary
      // that quietly lost half its lines should say so.
      dropped: gatedSteps.dropped.length + gatedOutcome.dropped.length,
      ops: ops.length,
      opsShown: capped.length,
      model: model || null,
    },
  };
}

// ── the account's own cache ──────────────────────────────────────────────────
// Session-scoped and keyed exactly like the reading's result cache, so
// reopening a trace shows the account it already paid for instead of buying a
// second one. Same discipline as the reading: a re-read writes a new one.
const SUMMARY_CACHE = new Map();
const SUMMARY_CACHE_MAX = 20;

export function readSummaryCache(key) {
  return (key && SUMMARY_CACHE.get(key)) || null;
}
export function writeSummaryCache(key, summary) {
  if (!key) return;
  if (SUMMARY_CACHE.size >= SUMMARY_CACHE_MAX) {
    const oldest = SUMMARY_CACHE.keys().next().value;
    if (oldest !== undefined) SUMMARY_CACHE.delete(oldest);
  }
  SUMMARY_CACHE.set(key, summary);
}
export function clearSummaryCache(key) {
  if (key) SUMMARY_CACHE.delete(key);
}
